import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import type { HubTopicCard, HubTopicCardGroup } from "../data/navMenus";

export type { HubTopicCard, HubTopicCardGroup };

type HubTopicCardGridProps = {
  groups: HubTopicCardGroup[];
  sectionTitle?: string;
  sectionSubtitle?: string;
};

function TopicCard({ card }: { card: HubTopicCard }) {
  const accent = card.accent ?? "teal";
  const isTeal = accent === "teal";
  const Icon = card.icon;

  return (
    <Link
      to={card.href}
      className={`group flex flex-col h-full p-5 rounded-xl border transition-all duration-300 ${
        isTeal
          ? "border-teal-500/15 hover:border-teal-500/35 hover:bg-teal-500/5"
          : "border-emerald-500/15 hover:border-emerald-500/35 hover:bg-emerald-500/5"
      }`}
      style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
    >
      <div className="flex items-start gap-4 flex-1">
        {Icon && (
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
              isTeal ? "bg-teal-500/10 group-hover:bg-teal-500/20" : "bg-emerald-500/10 group-hover:bg-emerald-500/20"
            }`}
          >
            <Icon size={20} className={isTeal ? "text-teal-400" : "text-emerald-400"} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-slate-200 text-sm group-hover:text-slate-100 transition-colors mb-1.5" style={{ fontWeight: 600 }}>
            {card.label}
          </div>
          {card.summary && (
            <p className="text-on-dark-muted text-xs leading-relaxed line-clamp-3">{card.summary}</p>
          )}
        </div>
      </div>
      <span
        className={`inline-flex items-center gap-1 text-xs mt-4 transition-colors ${
          isTeal ? "text-teal-400/80 group-hover:text-teal-300" : "text-emerald-400/80 group-hover:text-emerald-300"
        }`}
        style={{ fontWeight: 500 }}
      >
        View full brief
        <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

export function HubTopicCardGrid({ groups, sectionTitle, sectionSubtitle }: HubTopicCardGridProps) {
  return (
    <section className="py-16 lg:py-20 border-b border-slate-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(sectionTitle || sectionSubtitle) && (
          <div className="text-center mb-12">
            {sectionTitle && (
              <h2
                className="text-slate-100"
                style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
              >
                {sectionTitle}
              </h2>
            )}
            {sectionSubtitle && <p className="text-on-dark-muted mt-3 text-base max-w-2xl mx-auto">{sectionSubtitle}</p>}
          </div>
        )}

        <div className="flex flex-col gap-12">
          {groups.map((group) => (
            <div key={group.heading} className={group.dimmed ? "opacity-60" : undefined}>
              {group.dimmed ? (
                <div className="flex items-center gap-3 mb-4">
                  <p className="text-slate-500 text-xs uppercase tracking-widest" style={{ fontWeight: 600 }}>
                    {group.heading}
                  </p>
                  <span className="px-2 py-0.5 rounded-full border border-slate-700/60 bg-slate-800/50 text-slate-500 text-[10px] font-semibold uppercase tracking-wide">
                    sold separately
                  </span>
                </div>
              ) : (
                <p className="text-teal-400 text-xs uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
                  {group.heading}
                </p>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.cards.map((card) => (
                  <TopicCard key={card.href} card={card} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
